// src/NarrativeEngine.js
import { LabGenerator } from "./LabGenerator.js";
import { stableHash } from "./coreRepoJSONL.js";
import { formatLabel } from "./Utils.js";

// Transforma nodos clínicos estériles en escenas vivas del hospital.
// No modifica los datos de origen — opera únicamente en la capa de presentación.
const SHIFT_CONTEXTS = {
  'theme-morning': {
    label: 'TURNO DE MAÑANA',
    ambient: ['Llegas puntual a la entrega de guardia.', 'El café apenas está listo.', 'El piso se siente tranquilo.'],
    phases: ['[FASE 1: ADMISIÓN]', '[FASE 2: ABORDAJE]', '[FASE 3: RESOLUCIÓN]']
  },
  'theme-afternoon': {
    label: 'TURNO DE TARDE',
    ambient: ['Pico de actividad en urgencias.', 'Las enfermeras corren por el pasillo.', 'El calor es agobiante en el piso.'],
    phases: ['[FASE 1: PRIORIZACIÓN]', '[FASE 2: INTERVENCIÓN]', '[FASE 3: ESTABILIZACIÓN]']
  },
  'theme-night': {
    label: 'TURNO DE NOCHE',
    ambient: ['3:00 AM. El hospital duerme, pero tú no.', 'Solo queda luz en el control de enfermería.', 'La fatiga empieza a pesar.'],
    phases: ['[FASE 1: ALERTA ROJA]', '[FASE 2: ACCIÓN CRÍTICA]', '[FASE 3: VIGILANCIA]']
  }
};

const TOPIC_INTROS = {
  'Cardiología': ['El monitor de telemetría emite una alarma aguda.', 'Enfermería reporta un cambio abrupto en el trazo EKG.', 'Te llaman de choque: dolor torácico.'],
  'Ginecología/Obstetricia': ['El tococardiógrafo empieza a marcar deceleraciones.', 'La obstetriz llama: algo no cuadra en la vigilancia fetal.', 'Te solicitan valorar a una paciente gestante.'],
  'Pediatría': ['La mamá del paciente de la cuna 6 está llorando.', 'El pediatra de guardia requiere apoyo urgente.', 'Ingresa un neonato con dificultad respiratoria.'],
  'Infectología': ['El laboratorio reporta valores críticos.', 'El paciente en aislamiento presenta nuevo pico febril.', 'Hemocultivos positivos reportados por microbiología.'],
  'Gastroenterología': ['Paciente presenta dolor abdominal agudo y vómito.', 'Llamada urgente por sangrado de tubo digestivo.', 'Ictericia y dolor en el cuadrante superior derecho.'],
  'Urgencias/Trauma': ['Llega una ambulancia a urgencias.', 'El código de activación suena en el altavoz del pasillo.', 'Un politraumatizado entra por la puerta de trauma.'],
  'Neurología': ['El paciente del cuarto 4 "no responde como antes".', 'Neurología reporta un cambio sutil en el estado mental.', 'Alerta de Código Ictus en el piso 3.'],
  'Psiquiatría': ['Agitación psicomotriz grave en piso de internamiento.', 'Interconsulta urgente por alteración conductual aguda.', 'Paciente ingresa con ideas de daño inminente.'],
  'Nefrología': ['Laboratorio reporta hiperkalemia severa.', 'Disminución drástica de la uresis en las últimas 6 horas.', 'Alteración del equilibrio ácido-base que requiere manejo.'],
  'Neumología': ['Desaturación severa reportada por enfermería.', 'Llaman de urgencias por paciente con franco tiraje intercostal.', 'Gasometría arterial con hipoxemia crítica.'],
  'Endocrinología': ['Una alarma de glucómetro rompe el silencio.', 'El sistema de monitoreo manda una alerta metabólica.', 'Laboratorio avisa de una crisis hiperglucémica.'],
  'Cirugía': ['Valoración urgente por abdomen agudo quirúrgico.', 'Paciente postoperado presenta signos de choque.', 'Interconsulta a piso de cirugía por complicación.'],
  'Medicina Interna': ['Descompensación aguda en el piso de medicina interna.', 'Enfermería reporta deterioro general sin causa clara.', 'Interconsulta por paciente polimórbido inestable.'],
  'default': ['El beeper vibra en tu bolsillo.', 'Enfermería activa la alarma de cama.', 'Recibes un aviso urgente en el sistema del hospital.']
};


const MENTOR_INTROS = {
  'ssr_antigravity': [
    '**Dr. Antigravité:** *(viendo el monitor)* — Vite, vite. No hay tiempo para dudar. ¿Qué hacemos?',
    '**Dr. Antigravité:** — Este momento lo decidiste antes de entrar al cuarto. Demuéstralo.',
    '**Dr. Antigravité:** — La física del tiempo no perdona. ¿Tu diagnóstico?'
  ],
  'ssr_wu_zhongli': [
    '**Dr. Wú:** *(en silencio, asiente)* — Este es el caso que separa al médico del residente. Decide.',
    '**Dr. Wú:** — Protocolo activado. Tu siguiente acción define el desenlace.',
    '**Dr. Wú:** — No existe segunda oportunidad en este estado del paciente. ¿Cuál es tu paso?'
  ],
  'sr_schwerkraftfrei': [
    '**Dra. Schwerkraftfrei:** — Elimina lo que no puede ser, lo que queda es la verdad. Procede.',
    '**Dra. Schwerkraftfrei:** — Eficiencia clínica. Sin rodeos. ¿Cuál es la acción con mayor impacto ahora mismo?',
    '**Dra. Schwerkraftfrei:** — La opción incorrecta tiene un costo. ¿Cuál descartarías primero?'
  ],
  'sr_neyron_a': [
    '**Dr. Neyron-A:** — Tu corteza prefrontal ya procesó esto. Confía en el patrón. ¿Qué ves?',
    '**Dr. Neyron-A:** — Reducción de ruido cognitivo en progreso. La respuesta está en el signo más prominente.',
    '**Dr. Neyron-A:** — El reconocimiento de patrones supera al razonamiento puro. ¿Qué te está diciendo el cuadro?'
  ],
  'r_kvantovaya': [
    '**MIP Kvantovaya:** — He calculado todos los resultados posibles. Algunos son mejores que otros. ¿Cuál eleges?',
    '**MIP Kvantovaya:** — Probabilísticamente hablando... pero mejor te lo dejo a ti.',
    '**MIP Kvantovaya:** — *(mirando el expediente)* — Interesante colisión de diagnósticos. ¿Cuál colapsa la incertidumbre?'
  ],
  'default': [
    '— ¿Cuál es tu próxima acción?',
    '— El paciente te espera. ¿Qué decides hacer?',
    '— Los datos están frente a ti. ¿Tu manejo?'
  ]
};

const LETHAL_WARNINGS = [
  '🔴 **ATENCIÓN:** Este caso tiene decisiones potencialmente fatales.',
  '⚡ **ZONA CRÍTICA:** Un error aquí puede ser irreversible.',
  '🚨 **AVISO DE SEGURIDAD:** Errores letales en este escenario.'
];

function mdBold(text) {
  if (!text) return '';
  return String(text).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Construye el HTML narrativo completo para un nodo clínico.
 * @param {Object} node - El nodo del CORE (con enunciado, labs, tags, etc.)
 * @param {Object} options - { shiftTheme, activeMentorId, caseIndex, totalCases }
 */
export function buildNarrativeWrapper(node, options = {}) {
  if (!node) {
    console.warn("[NarrativeEngine] Attempted to build narrative for null node.");
    return `<div class="narrative-frame"><h3>Error de Carga</h3><p>No se pudo recuperar la información de este caso.</p></div>`;
  }

  const { shiftTheme = 'theme-morning', activeMentorId = 'default', caseIndex = 0, isBlind = false, isChain = false } = options;
  const ctx = SHIFT_CONTEXTS[shiftTheme] || SHIFT_CONTEXTS['theme-morning'];

  // Seleccionar intro temática
  const topicKey = node.topic || node.tema || 'default';
  const topicPool = TOPIC_INTROS[topicKey] || TOPIC_INTROS['default'];
  const ambient = ctx.ambient[caseIndex % ctx.ambient.length];
  const topicIntro = topicPool[caseIndex % topicPool.length];

  // Voz del mentor (Determinista basada en el nodo para evitar parpadeo)
  const mentorId = activeMentorId || 'default';
  const mentorPool = MENTOR_INTROS[mentorId] || MENTOR_INTROS['default'];
  const nodeSeed = node.id ? parseInt(stableHash(node.id).slice(-4), 16) : 0;
  const mentorLine = mentorPool[nodeSeed % mentorPool.length];

  // Labs dinámicos
  const labsHTML = node.labs ? buildLabsHTML(node.labs, { nodeId: node.id }) : '';

  // Badge de dificultad / turno
  let phaseLabel;
  if (isChain) {
    const phases = ctx.phases || ['[FASE CLÍNICA]'];
    phaseLabel = phases[caseIndex % phases.length];
  } else {
    phaseLabel = `[PACIENTE ${caseIndex + 1}]`;
  }
  const isGhost = node.es_fantasma || (node.tags && node.tags.includes('error_real'));
  
  const lethalWarning = (node.letal_si_falla || (node.tags && node.tags.includes('letal')))
    ? `<div class="narrative-lethal-warning">${mdBold(LETHAL_WARNINGS[caseIndex % LETHAL_WARNINGS.length])}</div>`
    : '';

  const frameClass = isGhost ? 'narrative-frame ghost-file' : 'narrative-frame';
  const ghostBadge = isGhost ? '<span class="ghost-badge">EXPEDIENTE FANTASMA</span>' : '';
  const topicBadge = (node.subtema || node.topic) && !isBlind
    ? `<span class="topic-badge">${formatLabel(node.subtema || node.topic)}</span>`
    : '';

  try {
    return `
      <div class="${frameClass}">
        <div class="narrative-badge">
          <span class="shift-badge">${phaseLabel}</span>
          ${ghostBadge}
          ${topicBadge}
        </div>


        <div class="narrative-ambient">
          ${isGhost ? mdBold('🌑 **ERROR HISTÓRICO:** ') : ''}
          ${mdBold(ambient)} ${mdBold(topicIntro)}
        </div>

        <div class="narrative-case-body">
          ${lethalWarning}
          <p class="narrative-enunciado">${mdBold(node.enunciado || 'Enunciado no disponible.')}</p>
          ${labsHTML}
        </div>

        <div class="narrative-mentor-line">${mdBold(mentorLine)}</div>
        ${isGhost ? '<div class="ghost-footer">Este caso está basado en un evento clínico real con desenlace fatal.</div>' : ''}
      </div>
  `;
  } catch (e) {
    console.error("[NarrativeEngine] Critical error building template:", e);
    return `<div class="narrative-frame"><h3>Fallo Crítico de Renderizado</h3></div>`;
  }
}

function buildLabsHTML(labs, options = {}) {
  if (!labs || typeof labs !== 'object') return '';
  
  // Si tiene más de 3 parámetros, usamos el generador de tablas profesional
  const keys = Object.keys(labs);
  if (keys.length > 3) {
      return LabGenerator.generateHTML(labs, options);
  }

  const labNames = {
    Glu: 'Glucosa', pH: 'pH', HCO3: 'HCO₃', Lac: 'Lactato',
    FC: 'FC', TA: 'TA', Sat: 'SatO₂', Cr: 'Creatinina',
    Hb: 'Hemoglobina', LEUCOS: 'Leucocitos', PCR: 'PCR',
    Na: 'Sodio', K: 'Potasio', INR: 'INR', BUN: 'BUN',
    TAS: 'T/A Sistólica', PAD: 'T/A Diastólica', FR: 'FR', T: 'Temperatura'
  };

  const dangerZones = {
    Glu: v => v > 400 || v < 50, pH: v => v < 7.30 || v > 7.50,
    K: v => v < 3.0 || v > 6.0, Lac: v => v > 2.0,
    HCO3: v => v < 15 || v > 30, Sat: v => v < 94
  };

  const entries = Object.entries(labs).filter(([k]) => labNames[k]);
  if (entries.length === 0) return '';

  const pills = entries.map(([key, val]) => {
    const isDanger = dangerZones[key]?.(Number(val)) ?? false;
    const display = labNames[key] || key;
    return `<span class="lab-pill ${isDanger ? 'lab-danger' : ''}">${display}: <strong>${val}</strong></span>`;
  }).join('');

  return `<div class="labs-strip">${pills}</div>`;
}
